import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, Textarea, statusBadge, formatDate, Badge } from '../ui';
import { Plus, Filter } from 'lucide-react';

export default function Registrations() {
  const { registrations, teams, tournaments, payments, addRegistration, updateRegistration } = useApp();
  const { isAdmin } = useAuth();
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState('all');
  const [filterTrn, setFilterTrn] = useState('all');
  const [form, setForm] = useState({ teamId: '', tournamentId: '', ageGroup: 'U12', coachName: '', email: '', phone: '', notes: '' });
  const [errors, setErrors] = useState({});

  const getTeam = id => teams.find(t => t.id === id);
  const getTournament = id => tournaments.find(t => t.id === id);
  const getPayment = id => payments.find(p => p.id === id);

  const filtered = registrations.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (filterTrn !== 'all' && r.tournamentId !== filterTrn) return false;
    return true;
  });

  const validate = () => {
    const e = {};
    if (!form.teamId) e.teamId = 'Required';
    if (!form.tournamentId) e.tournamentId = 'Required';
    if (!form.email) e.email = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    addRegistration({ ...form, status: 'incomplete', paymentId: null });
    setShowNew(false);
    setForm({ teamId: '', tournamentId: '', ageGroup: 'U12', coachName: '', email: '', phone: '', notes: '' });
  };

  const statusCounts = {
    all: registrations.length,
    confirmed: registrations.filter(r => r.status === 'confirmed').length,
    incomplete: registrations.filter(r => r.status === 'incomplete').length,
    waitlisted: registrations.filter(r => r.status === 'waitlisted').length,
  };

  const selectedTournament = form.tournamentId ? getTournament(form.tournamentId) : null;

  return (
    <div>
      <PageHeader title="Registrations" subtitle={`${registrations.length} total registrations`}
        actions={isAdmin && <Button variant="primary" onClick={() => setShowNew(true)}><Plus size={15} /> New registration</Button>}
      />

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', background: 'var(--surface-2)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['all','All'], ['confirmed','Confirmed'], ['incomplete','Incomplete'], ['waitlisted','Waitlisted']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: filter === val ? 'var(--surface-0)' : 'transparent', color: filter === val ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: filter === val ? 'var(--shadow-sm)' : 'none', transition: 'all 0.15s' }}>
            {label} <span style={{ fontSize: 11, opacity: 0.7 }}>({statusCounts[val]})</span>
          </button>
        ))}
      </div>

      {/* Tournament filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.25rem', alignItems: 'center' }}>
        <Filter size={15} color="var(--text-muted)" />
        <Select style={{ width: 'auto' }} value={filterTrn} onChange={e => setFilterTrn(e.target.value)}>
          <option value="all">All tournaments</option>
          {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </div>

      <Card padding="0">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--surface-1)' }}>
              {['Team','Age group','Tournament','Registered','Status','Payment','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No registrations match your filters</td></tr>
            ) : filtered.map(reg => {
              const team = getTeam(reg.teamId);
              const tourn = getTournament(reg.tournamentId);
              const pay = getPayment(reg.paymentId);
              return (
                <tr key={reg.id} style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-1)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: team?.color || '#94a3b8', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600 }}>{team?.name || '—'}</span>
                    </div>
                  </td>
                  <td style={{ padding: '11px 14px' }}>{reg.ageGroup}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tourn?.name}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(reg.createdAt)}</td>
                  <td style={{ padding: '11px 14px' }}>{statusBadge(reg.status)}</td>
                  <td style={{ padding: '11px 14px' }}>{pay ? statusBadge(pay.status) : <Badge variant="gray">No invoice</Badge>}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {isAdmin && reg.status === 'incomplete' && (
                        <button onClick={() => updateRegistration(reg.id, { status: 'confirmed' })}
                          style={{ fontSize: 12, color: '#6eca7c', background: 'rgba(40,120,60,0.2)', border: 'none', padding: '3px 8px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
                      )}
                      <button style={{ fontSize: 12, color: 'var(--gold-main)', background: 'none', border: 'none', cursor: 'pointer' }}>View</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* New Registration Modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="New registration" width={580}>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FormField label="Team" required error={errors.teamId}>
              <Select value={form.teamId} onChange={e => setForm(p => ({...p, teamId: e.target.value}))}>
                <option value="">Select team…</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Tournament / league" required error={errors.tournamentId}>
              <Select value={form.tournamentId} onChange={e => setForm(p => ({...p, tournamentId: e.target.value}))}>
                <option value="">Select event…</option>
                {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Age group" required>
              <Select value={form.ageGroup} onChange={e => setForm(p => ({...p, ageGroup: e.target.value}))}>
                {(selectedTournament?.ageGroups?.map(ag => ag.group) || ['U8','U10','U12','U14','U16','U19']).map(ag => (
                  <option key={ag}>{ag}</option>
                ))}
              </Select>
            </FormField>
            <FormField label="Head coach name">
              <Input value={form.coachName} onChange={e => setForm(p => ({...p, coachName: e.target.value}))} placeholder="Coach name" />
            </FormField>
            <FormField label="Contact email" required error={errors.email}>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="coach@example.com" />
            </FormField>
            <FormField label="Contact phone">
              <Input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="(555) 000-0000" />
            </FormField>
          </div>
          <FormField label="Notes / special requests">
            <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Any accommodations, scheduling conflicts, or notes…"
              rows={3} style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-sans)', resize: 'vertical' }} />
          </FormField>
          {selectedTournament && (
            <div style={{ background: 'var(--gold-pale)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--gold-deep)' }}>
              <strong>{selectedTournament.name}</strong> · {selectedTournament.dates?.join(' – ')} · {selectedTournament.location}
              {selectedTournament.ageGroups?.find(ag => ag.group === form.ageGroup) && (
                <span> · Fee: <strong>${selectedTournament.ageGroups.find(ag => ag.group === form.ageGroup).fee}</strong></span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <Button type="button" variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Submit registration</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
